import { Link, useParams } from "react-router-dom";
import useSeo from "../../hooks/useSeo";
import { works } from "../../mocks/works";
import { categories } from "../../mocks/categories";
import { getCategoryName } from "../../components/feature/WorkCard";

const sampleChapters = [
  "第一章 火种初燃",
  "第二章 夜袭之影",
  "第三章 试炼之门",
  "第四章 同伴之名",
  "第五章 血与风",
  "第六章 月下之约",
  "第七章 断刃重逢",
  "第八章 影之抉择",
];

export default function WorkDetail() {
  const { id } = useParams();
  const work = works.find((w) => w.id === id);

  useSeo({
    title: work
      ? `${work.title} - 火影忍者同人作品详情 | 忍界同人志`
      : "作品未找到 | 忍界同人志",
    description: work
      ? `${work.title}，作者${work.author}。${work.summary} 查看章节、更新进度与同人推荐。`
      : undefined,
    jsonLd: work
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: work.title,
          author: { "@type": "Person", name: work.author },
          articleSection: getCategoryName(work.categoryId),
          inLanguage: "zh-CN",
          image: work.cover,
          keywords: work.tags.join(","),
          url: `https://wuxf.cn/work/${work.id}`,
        }
      : undefined,
  });

  if (!work) {
    return (
      <div className="pt-16 md:pt-[72px]">
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">作品不存在或已下架</h1>
          <p className="mt-3 text-sm text-foreground-400">你查找的同人作品可能已被移除，返回作品库看看其他内容吧。</p>
          <Link to="/works" className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 whitespace-nowrap">
            返回作品库
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.id === work.categoryId);
  const related = [
    ...works.filter((w) => w.categoryId === work.categoryId && w.id !== work.id),
    ...works.filter((w) => w.categoryId !== work.categoryId),
  ].slice(0, 4);

  return (
    <div className="pt-16 md:pt-[72px]">
      <nav aria-label="面包屑" className="mx-auto max-w-7xl px-4 md:px-6 py-4 text-xs text-foreground-400">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="transition-colors hover:text-primary-300">首页</Link></li>
          <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
          <li><Link to="/works" className="transition-colors hover:text-primary-300">作品库</Link></li>
          <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
          <li><Link to={`/works?cat=${work.categoryId}`} className="transition-colors hover:text-primary-300">{getCategoryName(work.categoryId)}</Link></li>
          <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
          <li className="text-foreground-600">{work.title}</li>
        </ol>
      </nav>

      <section className="mx-auto max-w-7xl px-4 md:px-6 pb-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="sticky top-20 overflow-hidden rounded-xl border border-background-300/60 bg-background-100">
              <div className="aspect-[3/4] w-full">
                <img src={work.cover} alt={`${work.title} 同人作品封面`} title={`${work.title} 火影忍者同人作品`} className="h-full w-full object-cover object-top" />
              </div>
              <div className="p-5">
                <div className="flex flex-wrap gap-2">
                  {work.tags.map((t) => (
                    <span key={t} className="rounded-md bg-background-200/80 px-2.5 py-1 text-xs text-foreground-400">
                      #{t}
                    </span>
                  ))}
                </div>
                <dl className="mt-5 space-y-3 border-t border-background-300/60 pt-4 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-400">状态</dt>
                    <dd className="text-foreground-800">{work.status}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-400">最新章节</dt>
                    <dd className="text-foreground-800">第 {work.chapters} 章</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-400">字数</dt>
                    <dd className="text-foreground-800">{work.words}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-400">热度</dt>
                    <dd className="text-foreground-800">{work.views} 阅读</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-400">收藏</dt>
                    <dd className="text-foreground-800">{work.favorites}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-400">评分</dt>
                    <dd className="flex items-center gap-1 text-primary-300">
                      <i className="ri-star-fill" /> {work.rating.toFixed(1)}
                    </dd>
                  </div>
                </dl>
                <div className="mt-5 flex gap-2">
                  <button type="button" className="flex flex-1 items-center justify-center gap-2 rounded-md bg-primary-500 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap">
                    <i className="ri-book-open-line" />开始阅读
                  </button>
                  <button type="button" className="flex items-center justify-center gap-1 rounded-md border border-background-300/70 px-4 py-2.5 text-sm text-foreground-300 transition-colors hover:border-accent-500/50 hover:text-accent-500 whitespace-nowrap">
                    <i className="ri-heart-line" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500/10 px-2.5 py-1 text-xs font-medium text-primary-300">
                {category?.name}
              </span>
              <span className="rounded-md bg-accent-500/10 px-2.5 py-1 text-xs text-accent-500">
                {work.status}
              </span>
              <span className="text-xs text-foreground-400">{work.updated}</span>
            </div>
            <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
              {work.title}
            </h1>
            <p className="mt-3 flex items-center gap-2 text-sm text-foreground-400">
              <i className="ri-user-line" />
              作者：<span className="text-foreground-700">{work.author}</span>
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground-500">{work.summary}</p>

            <div className="mt-8 rounded-lg border border-background-300/60 bg-background-100 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-file-list-3-line text-primary-300" />
                作品简介
              </h2>
              <p className="mt-3 text-sm leading-loose text-foreground-300">{work.intro}</p>
            </div>

            <div className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
                <i className="ri-book-2-line text-primary-300" />
                章节目录
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {sampleChapters.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className="flex items-center gap-2 rounded-md border border-background-300/60 bg-background-100 px-4 py-2.5 text-left text-sm text-foreground-400 transition-colors hover:border-primary-500/50 hover:text-primary-300"
                  >
                    <i className="ri-file-text-line" />
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 md:px-6 pb-14">
        <div className="flex items-end justify-between">
          <div>
            <span className="text-xs font-semibold tracking-widest text-accent-500">RELATED</span>
            <h2 className="mt-1 font-heading text-2xl font-bold text-foreground-950">相关同人推荐</h2>
          </div>
          <Link to="/works" className="flex items-center gap-1 text-sm text-primary-300 hover:text-primary-400 whitespace-nowrap">
            更多作品 <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {related.map((r) => (
            <Link key={r.id} to={`/work/${r.id}`} className="group overflow-hidden rounded-lg border border-background-300/60 bg-background-100 transition-colors hover:border-primary-500/40">
              <div className="aspect-[3/4] overflow-hidden">
                <img src={r.cover} alt={`${r.title} 同人作品封面`} title={`${r.title} 相关火影忍者同人`} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-3">
                <h3 className="truncate font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-300">{r.title}</h3>
                <p className="mt-0.5 text-xs text-foreground-400">@{r.author}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}